/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-18 14:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Field } from 'redux-form';
import { FormLayout, TextStyle, Icon, Link } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';

import {
  CHANGE_PRODUCT_IMAGE_WHEN_ACTION_OPTIONS,
  ELEMENT_KEY_NAME,
  SWATCH_BY_OPTIONS
} from 'src/constants/theme-settings';
import { MENU_ID, SWATCH_SHAPE_OPTIONS } from 'src/constants/theme-settings.jsx';
import { DropdownIndicator, styles } from './react-select-style';
import { URL } from 'src/constants/links';

type Props = {
  formValues: any,
  prefix: string,
  filterDataOptions: any[],
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

class ProductItemSwatchForm extends Component<ComposedProps> {
  componentWillReceiveProps(nextProps) {
    const { formValues } = this.props;
    const { formValues: nextFormValues, updateField } = nextProps;
    const { keySwatch1, keySwatch2, keySwatch3 } = this.getKeySwatches(formValues);

    const showOptionValueOnHovering = _.get(
      formValues,
      `setting.${MENU_ID.SWATCHES}.showOptionValueOnHovering`
    );
    const nextShowOptionValueOnHovering = _.get(
      nextFormValues,
      `setting.${MENU_ID.SWATCHES}.showOptionValueOnHovering`
    );
    if (showOptionValueOnHovering !== nextShowOptionValueOnHovering) {
      updateField(`setting.${keySwatch1}.showOptionValueOnHovering`, nextShowOptionValueOnHovering);
      updateField(`setting.${keySwatch2}.showOptionValueOnHovering`, nextShowOptionValueOnHovering);
      updateField(`setting.${keySwatch3}.showOptionValueOnHovering`, nextShowOptionValueOnHovering);
    }

    const changeProductImageOn = _.get(
      formValues,
      `setting.${MENU_ID.SWATCHES}.changeProductImageOn`
    );
    const nextChangeProductImageOn = _.get(
      nextFormValues,
      `setting.${MENU_ID.SWATCHES}.changeProductImageOn`
    );
    if (changeProductImageOn !== nextChangeProductImageOn) {
      updateField(`setting.${keySwatch1}.changeProductImageOn`, nextChangeProductImageOn);
      updateField(`setting.${keySwatch2}.changeProductImageOn`, nextChangeProductImageOn);
      updateField(`setting.${keySwatch3}.changeProductImageOn`, nextChangeProductImageOn);
    }
  }

  handleToggleCollapse = name => {
    this.setState({
      [name]: !this.state[name]
    });
  };

  getFilterDataOptions = () => {
    const { prefix: activeMenuItem, formValues, filterDataOptions } = this.props;
    const [, menuId] = activeMenuItem.split('_');
    const otherSwatchKeys = _(formValues)
      .keys()
      .filter(key => key.includes('swatches') && !key.includes(menuId))
      .value();

    const otherSwatchValues = otherSwatchKeys.map(key => formValues[key]?.swatchType);
    return filterDataOptions.filter(item => !otherSwatchValues.includes(item.name));
  };

  getMoreText = () => {
    const { i18n } = this.props;
    return `+{{${i18n.translate(
      'AdminHTML.ThemeSetting.Form.swatchItem_MoreText_Filled1'
    )}}} ${i18n.translate('AdminHTML.ThemeSetting.Form.swatchItem_MoreText_Filled2')}`;
  };

  getKeySwatches = formValues => {
    const keyFormValues = Object.keys(_.get(formValues, 'setting', {}));
    const keySwatch1 = keyFormValues.find(key => key.includes(ELEMENT_KEY_NAME.SWATCH_1));
    const keySwatch2 = keyFormValues.find(key => key.includes(ELEMENT_KEY_NAME.SWATCH_2));
    const keySwatch3 = keyFormValues.find(key => key.includes(ELEMENT_KEY_NAME.SWATCH_3));
    return {
      keySwatch1,
      keySwatch2,
      keySwatch3
    };
  };

  render() {
    const { i18n, formValues } = this.props;
    const filterDataOptions = this.getFilterDataOptions();
    const formattedSwatchType = (filterDataOptions || []).map(item => ({
      label: item.originalName,
      value: item.name
    }));

    const formattedChangeProductImageWhenAction = CHANGE_PRODUCT_IMAGE_WHEN_ACTION_OPTIONS.map(
      item => ({
        label: i18n.translate(item.label),
        value: item.value
      })
    );

    const formattedSwatchBy = SWATCH_BY_OPTIONS.map(item => ({
      label: i18n.translate(item.label),
      value: item.value
    }));

    const { keySwatch1, keySwatch2, keySwatch3 } = this.getKeySwatches(formValues);
    const isEnableFirstSwatch = _.get(formValues, `setting.${keySwatch1}.isEnable`);
    const isEnableSecondSwatch = _.get(formValues, `setting.${keySwatch2}.isEnable`);
    const isEnableThirdSwatch = _.get(formValues, `setting.${keySwatch3}.isEnable`);

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_UserGuide')}
            </TextStyle>{' '}
            <Link url={URL['NormalSwatchThemeSetting']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          <div className="form-section__item">
            <Field
              name={`setting.${MENU_ID.SWATCHES}.showOptionValueOnHovering`}
              component={RenderCheckbox}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.swatchItem_showOptionHovering')}
            />
          </div>

          {/* Change product image when have action */}
          <div className="form-section__item form-section__item--standard-spacing">
            <Field
              name={`setting.${MENU_ID.SWATCHES}.changeProductImageOn`}
              label={i18n.translate(
                'AdminHTML.ThemeSetting.Form.swatchItem_ChangeProductImageAction_Item'
              )}
              options={formattedChangeProductImageWhenAction}
              component={RenderSelect2}
              components={{ DropdownIndicator }}
              styles={styles}
              isSearchable={false}
            />
          </div>

          {/* First swatch */}
          <div className="form-section__item form-section__item--standard-spacing form-section__item--bordered-top">
            <p className="title title--medium">
              {i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_FirstSwatch_Title'
              )}
            </p>
          </div>

          <div className="form-section__item">
            <Field
              name={`setting.${keySwatch1}.isEnable`}
              component={RenderToggleSwitch}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_FirstSwatch_Enable'
              )}
            />
          </div>

          {isEnableFirstSwatch && (
            <>
              {/* Swatch type */}
              <div className="form-section__item form-section__item--standard-spacing">
                <Field
                  name={`setting.${keySwatch1}.swatchType`}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_SelectProductOption'
                  )}
                  options={formattedSwatchType}
                  component={RenderSelect2}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isDeepCompare
                  placeholder={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_SelectProductOption_Placeholder'
                  )}
                  placeholderEmpty={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.swatchItem_Type_Placeholder'
                  )}
                />
              </div>

              {/* <div style={{ marginTop: '4px' }}>
                    <span className="form-link">
                      <TextStyle variation="subdued">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_electProductOption_Note1'
                        )}
                      </TextStyle>{' '}
                      <Link url={URL['NormalSwatchThemeSetting']} external>
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_SelectProductOption_Note2'
                        )}
                        <Icon source={ExternalMinor} color="base" />
                      </Link>
                      <span style={{ color: '#2C6ECB' }}>.</span>
                    </span>

                    <div style={{ marginTop: '12px' }}>
                      <Button fullWidth>
                        <span style={{ fontWeight: 500 }}>
                          {i18n.translate(
                            'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_UpdateSwatch'
                          )}
                        </span>
                      </Button>
                    </div>
                  </div> */}

              {/* Swatch by */}
              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${keySwatch1}.swatchBy`}
                  label={i18n.translate('AdminHTML.ThemeSetting.Form.swatchItem_By_Item')}
                  options={formattedSwatchBy}
                  component={RenderSelect2}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />

                <div style={{ marginTop: '4px' }}>
                  <span className="form-link">
                    <TextStyle variation="subdued">
                      {i18n.translate(
                        'AdminHTML.ShopifyIntegrationSettings.Form.productSwatchBy_UserGuide'
                      )}
                    </TextStyle>{' '}
                    <Link url={URL['SwatchItemDoc']} external>
                      {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
                      <Icon source={ExternalMinor} color="base" />
                    </Link>
                    <span style={{ color: '#2C6ECB' }}>.</span>
                  </span>
                </div>
              </div>

              {/* Swatch shape */}
              <div className="form-section__item form-section__item--medium-spacing">
                <p className="title">
                  {i18n.translate('AdminHTML.ThemeSetting.Form.swatchItem_Shape_Item')}
                </p>
                <div className="radio-button-wrapper radio-button-wrapper--large radio-button-wrapper--outline radio-button-wrapper--no-wrap">
                  {SWATCH_SHAPE_OPTIONS.map((item, index) => (
                    <Field
                      key={index}
                      name={`setting.${keySwatch1}.shape`}
                      id={item.value}
                      component={RenderRadioButton2}
                      label={item.icon}
                      isShowIconCheck
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Second swatch */}
          <div className="form-section__item form-section__item--standard-spacing form-section__item--bordered-top">
            <p className="title title--medium">
              {i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_SecondSwatch_Title'
              )}
            </p>
          </div>

          <div className="form-section__item">
            <Field
              name={`setting.${keySwatch2}.isEnable`}
              component={RenderToggleSwitch}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_SecondSwatch_Enable'
              )}
            />
          </div>

          {isEnableSecondSwatch && (
            <>
              {/* Swatch type */}
              <div className="form-section__item form-section__item--standard-spacing">
                <Field
                  name={`setting.${keySwatch2}.swatchType`}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_SelectProductOption'
                  )}
                  options={formattedSwatchType}
                  component={RenderSelect2}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isDeepCompare
                  placeholder={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_SelectProductOption_Placeholder'
                  )}
                  placeholderEmpty={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.swatchItem_Type_Placeholder'
                  )}
                />
              </div>

              {/* Swatch by */}
              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${keySwatch2}.swatchBy`}
                  label={i18n.translate('AdminHTML.ThemeSetting.Form.swatchItem_By_Item')}
                  options={formattedSwatchBy}
                  component={RenderSelect2}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />

                <div style={{ marginTop: '4px' }}>
                  <span className="form-link">
                    <TextStyle variation="subdued">
                      {i18n.translate(
                        'AdminHTML.ShopifyIntegrationSettings.Form.productSwatchBy_UserGuide'
                      )}
                    </TextStyle>{' '}
                    <Link url={URL['SwatchItemDoc']} external>
                      {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
                      <Icon source={ExternalMinor} color="base" />
                    </Link>
                    <span style={{ color: '#2C6ECB' }}>.</span>
                  </span>
                </div>
              </div>

              {/* Swatch shape */}
              <div className="form-section__item form-section__item--medium-spacing">
                <p className="title">
                  {i18n.translate('AdminHTML.ThemeSetting.Form.swatchItem_Shape_Item')}
                </p>
                <div className="radio-button-wrapper radio-button-wrapper--large radio-button-wrapper--outline radio-button-wrapper--no-wrap">
                  {SWATCH_SHAPE_OPTIONS.map((item, index) => (
                    <Field
                      key={index}
                      name={`setting.${keySwatch2}.shape`}
                      id={item.value}
                      component={RenderRadioButton2}
                      label={item.icon}
                      isShowIconCheck
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Third swatch */}
          <div className="form-section__item form-section__item--standard-spacing form-section__item--bordered-top">
            <p className="title title--medium">
              {i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_ThirdSwatch_Title'
              )}
            </p>
          </div>

          <div className="form-section__item">
            <Field
              name={`setting.${keySwatch3}.isEnable`}
              component={RenderToggleSwitch}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_ThirdSwatch_Enable'
              )}
            />
          </div>

          {isEnableThirdSwatch && (
            <>
              {/* Swatch type */}
              <div className="form-section__item form-section__item--standard-spacing">
                <Field
                  name={`setting.${keySwatch3}.swatchType`}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_SelectProductOption'
                  )}
                  options={formattedSwatchType}
                  component={RenderSelect2}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isDeepCompare
                  placeholder={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productSwatch_SelectProductOption_Placeholder'
                  )}
                  placeholderEmpty={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.swatchItem_Type_Placeholder'
                  )}
                />
              </div>

              {/* Swatch by */}
              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${keySwatch3}.swatchBy`}
                  label={i18n.translate('AdminHTML.ThemeSetting.Form.swatchItem_By_Item')}
                  options={formattedSwatchBy}
                  component={RenderSelect2}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />

                <div style={{ marginTop: '4px' }}>
                  <span className="form-link">
                    <TextStyle variation="subdued">
                      {i18n.translate(
                        'AdminHTML.ShopifyIntegrationSettings.Form.productSwatchBy_UserGuide'
                      )}
                    </TextStyle>{' '}
                    <Link url={URL['SwatchItemDoc']} external>
                      {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
                      <Icon source={ExternalMinor} color="base" />
                    </Link>
                    <span style={{ color: '#2C6ECB' }}>.</span>
                  </span>
                </div>
              </div>

              {/* Swatch shape */}
              <div className="form-section__item form-section__item--medium-spacing">
                <p className="title">
                  {i18n.translate('AdminHTML.ThemeSetting.Form.swatchItem_Shape_Item')}
                </p>
                <div className="radio-button-wrapper radio-button-wrapper--large radio-button-wrapper--outline radio-button-wrapper--no-wrap">
                  {SWATCH_SHAPE_OPTIONS.map((item, index) => (
                    <Field
                      key={index}
                      name={`setting.${keySwatch3}.shape`}
                      id={item.value}
                      component={RenderRadioButton2}
                      label={item.icon}
                      isShowIconCheck
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(ProductItemSwatchForm);
